import {DatePipe} from "@angular/common"
import {ChangeDetectionStrategy, Component, inject, input} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {RouterLink} from "@angular/router"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {JobInstanceHistoryGQL, JobInstanceHistoryQuery} from "@platform/components/jobs/job-instance-details/job-instance-details.generated"
import {from, map, startWith, switchMap} from "rxjs"

type HistoryEvent = JobInstanceHistoryQuery["jobInstanceHistory"][number]

type LoadState = {loading: boolean; events: HistoryEvent[]}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-instance-details",
    templateUrl: "job-instance-details.component.html",
    styleUrls: ["job-instance-details.component.scss"],
    imports: [RouterLink, DatePipe],
})
export class JobInstanceDetailsComponent {
    /** The VM name, bound from the `:instanceName` route param (withComponentInputBinding). */
    readonly $instanceName = input.required<string>({alias: "instanceName"})

    private readonly gql = inject(JobInstanceHistoryGQL)

    // Re-fetch whenever the instance name changes; emit a loading state first so the view can show it.
    private readonly $state = toSignal(
        toObservable(this.$instanceName).pipe(
            switchMap((instanceName) =>
                from(fetchThrowingErrors(this.gql)({instanceName})).pipe(
                    map((data): LoadState => ({loading: false, events: data.jobInstanceHistory})),
                    startWith<LoadState>({loading: true, events: []}),
                ),
            ),
        ),
        {initialValue: {loading: true, events: []} satisfies LoadState},
    )

    readonly $loading = () => this.$state().loading
    readonly $events = () => this.$state().events

    /** A short one-line summary of the event's payload, whichever source it came from. */
    detail(event: HistoryEvent): string {
        if (event.source === "instance") {
            const status = event.previousStatus && event.status ? `${event.previousStatus} → ${event.status}` : (event.status ?? "")
            return [status, event.reason].filter(Boolean).join(" · ")
        }
        return [event.taskType, event.advanceReason, event.message].filter(Boolean).join(" · ")
    }

    /** Colour class for the event chip, so preemptions/failures stand out when scanning. */
    tone(event: HistoryEvent): string {
        const t = event.eventType.toLowerCase()
        if (t.includes("preempt")) return "warn"
        if (t.includes("fail") || t.includes("gone") || t.includes("fatal") || t.includes("deleted")) return "bad"
        if (t.includes("complet") || t.includes("running")) return "good"
        return "muted"
    }
}
